import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import expenseService from '../../services/expenseService';
import paymentService from '../../services/paymentService';
import './Expenses.css';

const Expenses = () => {
  const { user } = useContext(AuthContext);
  const subsidiary = localStorage.getItem('selectedSubsidiaryKey') || 'LH';

  // Permission checks
  const checkPermission = (permissionName) => {
    if (!user?.permissions || !Array.isArray(user.permissions)) return false;

    return user.permissions.some(p => {
      // Handle permission objects with 'name' property
      if (typeof p === 'object' && p.name) {
        return p.name === permissionName;
      }
      // Handle plain string permissions
      if (typeof p === 'string') {
        return p === permissionName;
      }
      return false;
    });
  };

  const hasCreatePermission = checkPermission('expenses.create');
  const hasUpdatePermission = checkPermission('expenses.update');
  const hasDeletePermission = checkPermission('expenses.delete');
  const hasReadPermission = checkPermission('expenses.read');
  const hasPayPermission = checkPermission('expenses.pay');
  const hasPayAdminPermission = checkPermission('expenses.payadmin');

  const [expenses, setExpenses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [expenseItems, setExpenseItems] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    expense_state: '',
    expense_date: '',
    vendor_id: '',
    expense_item_id: '',
    created_by_id: '',
    subsidiary: subsidiary
  });

  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [expenseForm, setExpenseForm] = useState({
    name: '',
    expense_date: new Date().toISOString().split('T')[0],
    expense_state: 'Fulfilled',
    expense_amount: '',
    expense_paid: '',
    vendor_id: '',
    vendor_name: '',
    expense_item_id: '',
    subsidiary: subsidiary,
    payment_type_id: '',
    payment_date: '',
    tds_percentage: '',
    tds_amount: 0
  });

  // Autocomplete state for vendor in form
  const [vendorInput, setVendorInput] = useState('');
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);

  // Autocomplete state for expense item in form
  const [expenseItemInput, setExpenseItemInput] = useState('');
  const [filteredExpenseItems, setFilteredExpenseItems] = useState([]);
  const [showExpenseItemDropdown, setShowExpenseItemDropdown] = useState(false);

  // Autocomplete state for vendor in search form
  const [searchVendorInput, setSearchVendorInput] = useState('');
  const [filteredSearchVendors, setFilteredSearchVendors] = useState([]);
  const [showSearchVendorDropdown, setShowSearchVendorDropdown] = useState(false);

  // Autocomplete state for expense item in search form
  const [searchExpenseItemInput, setSearchExpenseItemInput] = useState('');
  const [filteredSearchExpenseItems, setFilteredSearchExpenseItems] = useState([]);
  const [showSearchExpenseItemDropdown, setShowSearchExpenseItemDropdown] = useState(false);

  // Autocomplete state for employee in search form
  const [searchEmployeeInput, setSearchEmployeeInput] = useState('');
  const [filteredSearchEmployees, setFilteredSearchEmployees] = useState([]);
  const [showSearchEmployeeDropdown, setShowSearchEmployeeDropdown] = useState(false);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedExpenseForPayment, setSelectedExpenseForPayment] = useState(null);
  const [isPaymentReadOnly, setIsPaymentReadOnly] = useState(false);
  const [paymentNames, setPaymentNames] = useState([]);
  const [paymentLines, setPaymentLines] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    payment_id: '',
    payment_date: '',
    payment_amount: '',
    balance_amount: ''
  });

  const expenseStates = ['Entered', 'Fulfilled', 'Unpaid', 'Paid', 'Payment Initiated'];

  useEffect(() => {
    fetchData();
  }, [filters, searchTerm]);

  useEffect(() => {
    fetchVendors();
    fetchExpenseItems();
    fetchPaymentTypes();
    fetchEmployees();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const expensesRes = await expenseService.getAllExpenses({
        ...filters,
        expense_no: searchTerm,
        name: searchTerm  // Search by name or number
      });

      if (expensesRes.success) {
        setExpenses(Array.isArray(expensesRes.data) ? expensesRes.data : []);
      }
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const vendorsRes = await expenseService.getVendors();
      if (vendorsRes.success) {
        setVendors(Array.isArray(vendorsRes.data) ? vendorsRes.data : []);
      }
    } catch (err) {
      console.error('Error fetching vendors:', err);
    }
  };

  const fetchExpenseItems = async () => {
    try {
      const itemsRes = await expenseService.getExpenseItems(subsidiary);
      if (itemsRes.success) {
        setExpenseItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);
      }
    } catch (err) {
      console.error('Error fetching expense items:', err);
    }
  };

  const fetchPaymentTypes = async () => {
    try {
      const typesRes = await expenseService.getPaymentTypes();
      if (typesRes.success) {
        setPaymentTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
      }
    } catch (err) {
      console.error('Error fetching payment types:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const employeesRes = await expenseService.getEmployees();
      if (employeesRes.success) {
        setEmployees(Array.isArray(employeesRes.data) ? employeesRes.data : []);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSearchVendorInput('');
    setSearchExpenseItemInput('');
    setSearchEmployeeInput('');
    setFilters({
      expense_state: 'Unpaid',
      expense_date: '',
      vendor_id: '',
      expense_item_id: '',
      created_by_id: '',
      subsidiary: subsidiary
    });
  };

  const handleExpenseFormChange = (e) => {
    const { name, value } = e.target;
    setExpenseForm(prev => {
      const updated = { ...prev, [name]: value };

      // Set payment_date to expense_date when non bank payment type is selected
      if (name === 'payment_type_id') {
        if (value !== '1' && value !== 1 && value != null && value !== '') {
          updated.payment_date = prev.expense_date;
        } else {
          updated.payment_date = '';
        }
      }

      // Recalculate TDS if expense amount changes and TDS percentage is set
      if (name === 'expense_amount' && prev.tds_percentage) {
        const amount = (parseFloat(prev.tds_percentage) / 100) * (parseFloat(value) || 0);
        updated.tds_amount = parseFloat(amount.toFixed(2));
      }

      return updated;
    });
  };

  const handleTdsChange = (e) => {
    const percentage = parseFloat(e.target.value) || 0;
    let amount = 0;

    if (percentage > 0) {
      amount = (percentage / 100) * (parseFloat(expenseForm.expense_amount) || 0);
      amount = parseFloat(amount.toFixed(2));
    }

    setExpenseForm(prev => ({
      ...prev,
      tds_percentage: e.target.value,
      tds_amount: amount
    }));
  };

  // Vendor autocomplete for form
  const handleVendorInputChange = (e) => {
    const value = e.target.value;
    setVendorInput(value);

    if (value.trim() === '') {
      setFilteredVendors([]);
      setShowVendorDropdown(false);
      setExpenseForm(prev => ({ ...prev, vendor_id: '', vendor_name: '' }));
    } else {
      const filtered = vendors.filter(vendor =>
        vendor.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredVendors(filtered);
      setShowVendorDropdown(true);
    }
  };

  const handleVendorSelect = (vendor) => {
    setVendorInput(vendor.name);
    setExpenseForm(prev => ({ ...prev, vendor_id: vendor.id, vendor_name: vendor.name }));
    setShowVendorDropdown(false);
    setFilteredVendors([]);
  };

  // Expense item autocomplete for form
  const handleExpenseItemInputChange = (e) => {
    const value = e.target.value;
    setExpenseItemInput(value);

    if (value.trim() === '') {
      setFilteredExpenseItems([]);
      setShowExpenseItemDropdown(false);
      setExpenseForm(prev => ({ ...prev, expense_item_id: '' }));
    } else {
      const filtered = expenseItems.filter(item =>
        item.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredExpenseItems(filtered);
      setShowExpenseItemDropdown(true);
    }
  };

  const handleExpenseItemSelect = (item) => {
    setExpenseItemInput(item.name);
    setExpenseForm(prev => ({ ...prev, expense_item_id: item.id }));
    setShowExpenseItemDropdown(false);
    setFilteredExpenseItems([]);
  };

  // Vendor autocomplete for search
  const handleSearchVendorInputChange = (e) => {
    const value = e.target.value;
    setSearchVendorInput(value);

    if (value.trim() === '') {
      setFilteredSearchVendors([]);
      setShowSearchVendorDropdown(false);
      setFilters(prev => ({ ...prev, vendor_id: '' }));
    } else {
      const filtered = vendors.filter(vendor =>
        vendor.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSearchVendors(filtered);
      setShowSearchVendorDropdown(true);
    }
  };

  const handleSearchVendorSelect = (vendor) => {
    setSearchVendorInput(vendor.name);
    setFilters(prev => ({ ...prev, vendor_id: vendor.id }));
    setShowSearchVendorDropdown(false);
    setFilteredSearchVendors([]);
  };

  // Expense item autocomplete for search
  const handleSearchExpenseItemInputChange = (e) => {
    const value = e.target.value;
    setSearchExpenseItemInput(value);

    if (value.trim() === '') {
      setFilteredSearchExpenseItems([]);
      setShowSearchExpenseItemDropdown(false);
      setFilters(prev => ({ ...prev, expense_item_id: '' }));
    } else {
      const filtered = expenseItems.filter(item =>
        item.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSearchExpenseItems(filtered);
      setShowSearchExpenseItemDropdown(true);
    }
  };

  const handleSearchExpenseItemSelect = (item) => {
    setSearchExpenseItemInput(item.name);
    setFilters(prev => ({ ...prev, expense_item_id: item.id }));
    setShowSearchExpenseItemDropdown(false);
    setFilteredSearchExpenseItems([]);
  };

  // Employee autocomplete for search
  const handleSearchEmployeeInputChange = (e) => {
    const value = e.target.value;
    setSearchEmployeeInput(value);

    if (value.trim() === '') {
      setFilteredSearchEmployees([]);
      setShowSearchEmployeeDropdown(false);
      setFilters(prev => ({ ...prev, created_by_id: '' }));
    } else {
      const filtered = employees.filter(emp =>
        emp.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSearchEmployees(filtered);
      setShowSearchEmployeeDropdown(true);
    }
  };

  const handleSearchEmployeeSelect = (employee) => {
    setSearchEmployeeInput(employee.name);
    setFilters(prev => ({ ...prev, created_by_id: employee.id }));
    setShowSearchEmployeeDropdown(false);
    setFilteredSearchEmployees([]);
  };

  const handleCreateNew = () => {
    setEditingExpense(null);
    setIsReadOnly(false);
    setExpenseForm({
      name: '',
      expense_date: new Date().toISOString().split('T')[0],
      expense_state: 'Fulfilled',
      expense_amount: '',
      expense_paid: '',
      vendor_id: '',
      vendor_name: '',
      expense_item_id: '',
      subsidiary: subsidiary,
      payment_type_id: '',
      payment_date: '',
      tds_percentage: '',
      tds_amount: 0
    });
    setVendorInput('');
    setExpenseItemInput('');
    setShowModal(true);
  };

  const handleEdit = async (expense, readOnly = false) => {
    setEditingExpense(expense);
    setIsReadOnly(readOnly);

    // Fix date conversion
    let formattedDate = '';
    if (expense.expense_date) {
      const date = new Date(expense.expense_date);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      formattedDate = `${year}-${month}-${day}`;
    }

    setExpenseForm({
      ...expense,
      expense_date: formattedDate,
      tds_percentage: expense.tds_percentage || '',
      tds_amount: expense.tds_amount || 0
    });

    setVendorInput(expense.vendor_name || '');
    setExpenseItemInput(expense.item_name || '');

    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validate expense item selection
    if (!expenseForm.expense_item_id) {
      setError('Please select a valid expense item from the list');
      return;
    }

    try {
      let response;
      if (editingExpense) {
        response = await expenseService.updateExpense(editingExpense.id, expenseForm);

        // If TDS percentage is provided and user has payment access
        if ((hasPayPermission || hasPayAdminPermission) && expenseForm.tds_percentage && parseFloat(expenseForm.tds_percentage) >= 0) {
          try {
            await expenseService.upsertTdsDeduction({
              po_exp: 'EXP',
              deduction_type: 'TDS',
              deduction_amount: expenseForm.tds_amount,
              po_exp_id: editingExpense.id
            });
          } catch (err) {
            console.error('Error saving TDS deduction:', err);
            setError('Expense updated but failed to save TDS deduction.');
            fetchData();
            return;
          }
        }
      } else {
        response = await expenseService.createExpense(expenseForm);
      }

      if (response.success) {
        setSuccessMessage(editingExpense ? 'Expense updated successfully!' : 'Expense created successfully!');
        setShowModal(false);
        setEditingExpense(null);
        resetForm();
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Save expense error:', err);
      setError(err.error || err.message || 'Failed to save expense');
    }
  };

  const handleDelete = async (expense) => {
    if (!window.confirm(`Are you sure you want to delete expense "${expense.expense_no}"?`)) {
      return;
    }

    try {
      const response = await expenseService.deleteExpense(expense.id);
      if (response.success) {
        setSuccessMessage('Expense deleted successfully!');
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Delete expense error:', err);
      setError(err.error || err.message || 'Failed to delete expense');
    }
  };

  // Payment Modal Functions
  const handlePaymentDetails = async (expense) => {
    setSelectedExpenseForPayment(expense);
    setShowPaymentModal(true);
    setError('');
    setSuccessMessage('');

    // Determine if modal should be read-only
    const shouldBeReadOnly = expense.expense_state === 'Paid' && hasPayPermission && !hasPayAdminPermission;
    setIsPaymentReadOnly(shouldBeReadOnly);

    // Fetch payment-related data
    try {
      const [paymentNamesRes, paymentLinesRes] = await Promise.all([
        paymentService.getPaymentNames(),
        paymentService.getPaymentLinesByTransaction(expense.id, 'EXP')
      ]);

      if (paymentNamesRes.success) {
        setPaymentNames(paymentNamesRes.data);
      }
      if (paymentLinesRes.success) {
        setPaymentLines(paymentLinesRes.data);
      }
    } catch (err) {
      console.error('Error fetching payment data:', err);
      setError('Failed to load payment data');
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedExpenseForPayment(null);
    setPaymentForm({
      payment_id: '',
      payment_date: '',
      payment_amount: '',
      balance_amount: ''
    });
    setPaymentLines([]);
    setPaymentNames([]);
  };

  const handlePaymentFormChange = async (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({ ...prev, [name]: value }));

    // Fetch payment details when payment is selected
    if (name === 'payment_id' && value) {
      try {
        const res = await paymentService.getPaymentDetails(value);
        if (res.success) {
          setPaymentForm(prev => ({
            ...prev,
            payment_date: res.data.payment_date ? new Date(res.data.payment_date).toISOString().split('T')[0] : '',
            balance_amount: res.data.balance_amount || '',
            payment_amount: selectedExpenseForPayment?.expense_amount || ''
          }));
        }
      } catch (err) {
        console.error('Error fetching payment details:', err);
      }
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const paymentData = {
        payment_id: paymentForm.payment_id,
        payment_type: 'EXP',
        type_transaction_id: selectedExpenseForPayment.id,
        payment_amount: paymentForm.payment_amount,
        subsidiary: subsidiary
      };

      const response = await paymentService.createPaymentLine(paymentData);
      if (response.success) {
        setSuccessMessage('Payment line added successfully!');
        // Refresh payment lines
        const paymentLinesRes = await paymentService.getPaymentLinesByTransaction(selectedExpenseForPayment.id, 'EXP');
        if (paymentLinesRes.success) {
          setPaymentLines(paymentLinesRes.data);
        }
        // Refresh payment names to update balances
        const paymentNamesRes = await paymentService.getPaymentNames();
        if (paymentNamesRes.success) {
          setPaymentNames(paymentNamesRes.data);
        }
        // Reset form
        setPaymentForm({
          payment_id: '',
          payment_date: '',
          payment_amount: '',
          balance_amount: ''
        });
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to add payment line');
      }
    } catch (err) {
      console.error('Payment submit error:', err);
      setError(err.error || err.message || 'Failed to add payment line');
    }
  };

  const handleDeletePaymentLine = async (line) => {
    if (!window.confirm('Are you sure you want to delete this payment line?')) {
      return;
    }

    try {
      const response = await paymentService.deletePaymentLine(line.id, line.payment_id, line.payment_amount);
      if (response.success) {
        setSuccessMessage('Payment line deleted successfully!');
        // Refresh payment lines
        const paymentLinesRes = await paymentService.getPaymentLinesByTransaction(selectedExpenseForPayment.id, 'EXP');
        if (paymentLinesRes.success) {
          setPaymentLines(paymentLinesRes.data);
        }
        // Refresh payment names to update balances
        const paymentNamesRes = await paymentService.getPaymentNames();
        if (paymentNamesRes.success) {
          setPaymentNames(paymentNamesRes.data);
        }
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to delete payment line');
      }
    } catch (err) {
      console.error('Delete payment line error:', err);
      setError(err.error || err.message || 'Failed to delete payment line');
    }
  };

  const handleMarkAsPaid = async (expense) => {
    // Check if payment type is selected
    if (!expenseForm.payment_type_id) {
      setError('Please select a payment type before marking as paid');
      return;
    }

    try {
      const payload = {
        ...expenseForm,
        expense_paid: 'Y',
        expense_state: 'Paid'
      };

      const response = await expenseService.updateExpense(expense.id, payload);
      if (response.success) {
        setSuccessMessage('Expense marked as paid successfully!');
        setShowModal(false);
        setEditingExpense(null);
        resetForm();
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Mark as paid error:', err);
      setError(err.error || err.message || 'Failed to mark expense as paid');
    }
  };

  const resetForm = () => {
    setExpenseForm({
      name: '',
      expense_date: new Date().toISOString().split('T')[0],
      expense_state: 'Fulfilled',
      expense_amount: '',
      expense_paid: '',
      vendor_id: '',
      vendor_name: '',
      expense_item_id: '',
      subsidiary: subsidiary,
      payment_type_id: '',
      payment_date: '',
      tds_percentage: '',
      tds_amount: 0
    });
    setVendorInput('');
    setExpenseItemInput('');
  };

  const filteredExpenses = expenses;

  return (
    <div className="expense-management">
      <div className="section-header">
        <div>
          <h2>Expense Management</h2>
          <p>Manage expenses and payments</p>
        </div>
        {hasCreatePermission && (
          <button className="btn-create" onClick={handleCreateNew}>
            + Create Expense
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Search and Filters */}
      <div className="search-filter-container">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search by expense number or name..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="filters-row">
          <div style={{ position: 'relative', minWidth: '200px' }}>
            <input
              type="text"
              className="filter-input"
              placeholder="Filter by vendor"
              value={searchVendorInput}
              onChange={handleSearchVendorInputChange}
              onFocus={() => {
                if (searchVendorInput && filteredSearchVendors.length > 0) {
                  setShowSearchVendorDropdown(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowSearchVendorDropdown(false), 200);
              }}
              autoComplete="off"
            />
            {showSearchVendorDropdown && filteredSearchVendors.length > 0 && (
              <div className="autocomplete-dropdown">
                {filteredSearchVendors.map((vendor) => (
                  <div
                    key={vendor.id}
                    onClick={() => handleSearchVendorSelect(vendor)}
                    className="autocomplete-item"
                  >
                    {vendor.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ position: 'relative', minWidth: '200px' }}>
            <input
              type="text"
              className="filter-input"
              placeholder="Filter by expense item"
              value={searchExpenseItemInput}
              onChange={handleSearchExpenseItemInputChange}
              onFocus={() => {
                if (searchExpenseItemInput && filteredSearchExpenseItems.length > 0) {
                  setShowSearchExpenseItemDropdown(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowSearchExpenseItemDropdown(false), 200);
              }}
              autoComplete="off"
            />
            {showSearchExpenseItemDropdown && filteredSearchExpenseItems.length > 0 && (
              <div className="autocomplete-dropdown">
                {filteredSearchExpenseItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleSearchExpenseItemSelect(item)}
                    className="autocomplete-item"
                  >
                    {item.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ position: 'relative', minWidth: '200px' }}>
            <input
              type="text"
              className="filter-input"
              placeholder="Filter by employee"
              value={searchEmployeeInput}
              onChange={handleSearchEmployeeInputChange}
              onFocus={() => {
                if (searchEmployeeInput && filteredSearchEmployees.length > 0) {
                  setShowSearchEmployeeDropdown(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowSearchEmployeeDropdown(false), 200);
              }}
              autoComplete="off"
            />
            {showSearchEmployeeDropdown && filteredSearchEmployees.length > 0 && (
              <div className="autocomplete-dropdown">
                {filteredSearchEmployees.map((emp) => (
                  <div
                    key={emp.id}
                    onClick={() => handleSearchEmployeeSelect(emp)}
                    className="autocomplete-item"
                  >
                    {emp.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <select
            name="expense_state"
            className="filter-select"
            value={filters.expense_state}
            onChange={handleFilterChange}
          >
            <option value="">All States</option>
            {expenseStates.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>

          <input
            type="date"
            name="expense_date"
            className="filter-input"
            value={filters.expense_date}
            onChange={handleFilterChange}
          />

          <button className="btn-reset" onClick={resetFilters}>
            Reset
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading expenses...</div>
      ) : (
        <>
          <div className="expenses-table-container">
            <table className="expenses-table">
              <thead>
                <tr>
                  <th>Expense Number</th>
                  <th>Name</th>
                  <th>Item Name</th>
                  <th>Vendor</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>State</th>
                  <th>Paid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="no-data">No expenses found</td>
                  </tr>
                ) : (
                  filteredExpenses.map(expense => (
                    <tr key={expense.id}>
                      <td><strong>{expense.expense_no || '-'}</strong></td>
                      <td>{expense.name || '-'}</td>
                      <td>{expense.item_name || '-'}</td>
                      <td>{expense.vendor_name || '-'}</td>
                      <td>{expense.expense_date ? new Date(expense.expense_date).toLocaleDateString() : '-'}</td>
                      <td>₹{expense.expense_amount || '0.00'}</td>
                      <td>
                        <span className={`state-badge state-${expense.expense_state?.toLowerCase().replace(' ', '-')}`}>
                          {expense.expense_state}
                        </span>
                      </td>
                      <td>{expense.expense_paid === 'Y' ? 'Yes' : 'No'}</td>
                      <td>
                        <div className="action-buttons">
                          {/* Edit icon: For non-Paid expenses with update permission OR Paid expenses with payadmin permission */}
                          {((expense.expense_state !== 'Paid' && hasUpdatePermission) || (expense.expense_state === 'Paid' && hasPayAdminPermission)) && (
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => handleEdit(expense, false)}
                              title="Edit Expense"
                            >
                              ✏️
                            </button>
                          )}

                          {/* View icon: Show when user only has read permission and no edit icon is shown */}
                          {hasReadPermission && !((expense.expense_state !== 'Paid' && hasUpdatePermission) || (expense.expense_state === 'Paid' && hasPayAdminPermission)) && (
                            <button
                              className="btn-icon btn-view"
                              onClick={() => handleEdit(expense, true)}
                              title="View Expense Details"
                            >
                              👁️
                            </button>
                          )}

                          {/* Delete icon: For non-Paid expenses with delete permission OR Paid expenses with payadmin permission */}
                          {((expense.expense_state !== 'Paid' && hasDeletePermission) || (expense.expense_state === 'Paid' && hasPayAdminPermission)) && (
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(expense)}
                              title="Delete Expense"
                            >
                              🗑️
                            </button>
                          )}
                          {(hasPayPermission || hasPayAdminPermission) && (
                            <button
                              className="btn-icon btn-pay"
                              onClick={() => handlePaymentDetails(expense)}
                              title="Payment Details"
                            >
                              ₹
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create/Edit Expense Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content expense-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isReadOnly ? 'View Expense Details' : (editingExpense ? 'Edit Expense' : 'Create New Expense')}</h3>
              <button className="close-button" onClick={() => { setShowModal(false); setEditingExpense(null); setIsReadOnly(false); resetForm(); }}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 60px)' }}>
              {error && <div className="error-message" style={{ margin: '0 20px 10px 20px' }}>{error}</div>}

              <div className="modal-body">
                {/* Expense Header Section */}
                <div className="form-section">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={expenseForm.name}
                        onChange={handleExpenseFormChange}
                        placeholder="Expense Name"
                        required
                        disabled={isReadOnly}
                      />
                    </div>

                    <div className="form-group date-field">
                      <label>Expense Date *</label>
                      <input
                        type="date"
                        name="expense_date"
                        value={expenseForm.expense_date}
                        onChange={handleExpenseFormChange}
                        required
                        disabled={isReadOnly}
                      />
                    </div>

                    <div className="form-group">
                      <label>Expense Item *</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          value={expenseItemInput}
                          onChange={handleExpenseItemInputChange}
                          onFocus={() => {
                            if (expenseItemInput && filteredExpenseItems.length > 0 && !isReadOnly) {
                              setShowExpenseItemDropdown(true);
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => setShowExpenseItemDropdown(false), 200);
                          }}
                          placeholder="Enter Expense Item"
                          required
                          autoComplete="off"
                          disabled={isReadOnly}
                        />
                        {showExpenseItemDropdown && filteredExpenseItems.length > 0 && (
                          <div className="autocomplete-dropdown">
                            {filteredExpenseItems.map((item) => (
                              <div
                                key={item.id}
                                onClick={() => handleExpenseItemSelect(item)}
                                className="autocomplete-item"
                              >
                                {item.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Amount *</label>
                      <input
                        type="number"
                        name="expense_amount"
                        value={expenseForm.expense_amount}
                        onChange={handleExpenseFormChange}
                        placeholder="Expense Amount"
                        required
                        disabled={isReadOnly}
                        step="0.01"
                      />
                    </div>

                    <div className="form-group">
                      <label>Expense State *</label>
                      <select
                        name="expense_state"
                        value={expenseForm.expense_state}
                        onChange={handleExpenseFormChange}
                        required
                        disabled={isReadOnly}
                      >
                        <option value="Entered">Entered</option>
                        <option value="Fulfilled">Fulfilled</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Vendor</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          value={vendorInput}
                          onChange={handleVendorInputChange}
                          onFocus={() => {
                            if (vendorInput && filteredVendors.length > 0 && !isReadOnly) {
                              setShowVendorDropdown(true);
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => setShowVendorDropdown(false), 200);
                          }}
                          placeholder="Enter Vendor Name"
                          autoComplete="off"
                          disabled={isReadOnly}
                        />
                        {showVendorDropdown && filteredVendors.length > 0 && (
                          <div className="autocomplete-dropdown">
                            {filteredVendors.map((vendor) => (
                              <div
                                key={vendor.id}
                                onClick={() => handleVendorSelect(vendor)}
                                className="autocomplete-item"
                              >
                                {vendor.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Expense Number</label>
                      <input
                        type="text"
                        name="expense_no"
                        value={expenseForm.expense_no || ''}
                        placeholder="Auto-generated"
                        readOnly
                        className="readonly-field"
                      />
                    </div>

                    {editingExpense && ((hasPayPermission && expenseForm.expense_state !== 'Paid') || hasPayAdminPermission) && (
                      <>
                        <div className="form-group">
                          <label>TDS Percentage</label>
                          <input
                            type="number"
                            name="tds_percentage"
                            value={expenseForm.tds_percentage}
                            onChange={handleTdsChange}
                            placeholder="TDS %"
                            min="0"
                            max="100"
                            step="0.01"
                            disabled={isReadOnly}
                          />
                        </div>

                        <div className="form-group">
                          <label>TDS Amount</label>
                          <input
                            type="number"
                            value={expenseForm.tds_amount}
                            readOnly
                            className="readonly-field"
                          />
                        </div>

                        <div className="form-group">
                          <label>Payment Type</label>
                          <select
                            name="payment_type_id"
                            value={expenseForm.payment_type_id}
                            onChange={handleExpenseFormChange}
                            disabled={isReadOnly}
                          >
                            <option value="">Select Payment Type</option>
                            {paymentTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {expenseForm.payment_type_id && expenseForm.payment_type_id !== '1' && expenseForm.payment_type_id !== 1 && (
                          <div className="form-group date-field">
                            <label>Payment Date</label>
                            <input
                              type="date"
                              name="payment_date"
                              value={expenseForm.payment_date || expenseForm.expense_date}
                              onChange={handleExpenseFormChange}
                              disabled={isReadOnly}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); setEditingExpense(null); setIsReadOnly(false); resetForm(); }}>
                  {isReadOnly ? 'Close' : 'Cancel'}
                </button>
                {!isReadOnly && (
                  <>
                    <button type="submit" className="btn-submit">
                      {editingExpense ? 'Update Expense' : 'Create Expense'}
                    </button>
                    {editingExpense && ((hasPayPermission && expenseForm.expense_state !== 'Paid') || hasPayAdminPermission) && (
                      <button
                        type="button"
                        className="btn-submit"
                        onClick={() => handleMarkAsPaid(editingExpense)}
                        disabled={!expenseForm.payment_type_id}
                        style={{ background: '#28a745' }}
                      >
                        Mark as Paid
                      </button>
                    )}
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {showPaymentModal && selectedExpenseForPayment && (
        <div className="modal-overlay" onClick={handleClosePaymentModal}>
          <div className="modal-content expense-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payment Details - Expense #{selectedExpenseForPayment.name} {isPaymentReadOnly && '(View Only)'}</h3>
              <button className="close-button" onClick={handleClosePaymentModal}>×</button>
            </div>

            <form onSubmit={handlePaymentSubmit} style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 60px)' }}>
              {error && <div className="error-message" style={{ margin: '0 20px 10px 20px' }}>{error}</div>}
              {successMessage && <div className="success-message" style={{ margin: '0 20px 10px 20px' }}>{successMessage}</div>}

              <div className="modal-body">
                {/* Expense Summary Section */}
                <div className="form-section">
                  <h4 style={{ marginBottom: '15px', color: '#2d3748', borderBottom: '2px solid #F57C00', paddingBottom: '8px' }}>
                    Expense Summary
                  </h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Expense Amount</label>
                      <input
                        type="text"
                        value={`₹${parseFloat(selectedExpenseForPayment.expense_amount || 0).toFixed(2)}`}
                        disabled
                        style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Balance Amount</label>
                      <input
                        type="text"
                        value={`₹${parseFloat(paymentForm.balance_amount || selectedExpenseForPayment.expense_amount || 0).toFixed(2)}`}
                        disabled
                        style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Form Section - Only show if not read-only */}
                {!isPaymentReadOnly && (
                  <div className="form-section">
                    <h4 style={{ marginBottom: '15px', color: '#2d3748', borderBottom: '2px solid #F57C00', paddingBottom: '8px' }}>
                      Add Payment
                    </h4>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Payment *</label>
                        <select
                          name="payment_id"
                          value={paymentForm.payment_id}
                          onChange={handlePaymentFormChange}
                          required
                        >
                          <option value="">Select Payment</option>
                          {paymentNames.map((payment) => (
                            <option key={payment.id} value={payment.id}>
                              {payment.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group date-field">
                        <label>Payment Date *</label>
                        <input
                          type="date"
                          name="payment_date"
                          value={paymentForm.payment_date}
                          onChange={handlePaymentFormChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Payment Amount *</label>
                        <input
                          type="number"
                          step="0.01"
                          name="payment_amount"
                          value={paymentForm.payment_amount}
                          onChange={handlePaymentFormChange}
                          placeholder="Enter amount"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Lines Table */}
                <div className="form-section">
                  <h4 style={{ marginBottom: '15px', color: '#2d3748', borderBottom: '2px solid #F57C00', paddingBottom: '8px' }}>
                    Payment History
                  </h4>
                  {paymentLines.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '12px 15px', textAlign: 'left' }}>Payment</th>
                            <th style={{ padding: '12px 15px', textAlign: 'left' }}>Date</th>
                            <th style={{ padding: '12px 15px', textAlign: 'right' }}>Amount</th>
                            <th style={{ padding: '12px 15px', textAlign: 'center' }}>Type</th>
                            {!isPaymentReadOnly && (
                              <th style={{ padding: '12px 15px', textAlign: 'center' }}>Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {paymentLines.map((line) => (
                            <tr key={line.id}>
                              <td style={{ padding: '12px 15px' }}>{line.name}</td>
                              <td style={{ padding: '12px 15px' }}>{new Date(line.payment_date).toLocaleDateString()}</td>
                              <td style={{ padding: '12px 15px', textAlign: 'right' }}>₹{parseFloat(line.payment_amount || 0).toFixed(2)}</td>
                              <td style={{ padding: '12px 15px', textAlign: 'center' }}>{line.payment_type}</td>
                              {!isPaymentReadOnly && (
                                <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                                  <button
                                    type="button"
                                    className="btn-icon btn-delete"
                                    onClick={() => handleDeletePaymentLine(line)}
                                    title="Delete Payment Line"
                                  >
                                    🗑️
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={{ textAlign: 'center', color: '#718096', padding: '20px', fontStyle: 'italic' }}>
                      No payment lines yet. Add a payment above to get started.
                    </p>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                {!isPaymentReadOnly && (
                  <button type="submit" className="btn-primary">
                    Add Payment
                  </button>
                )}
                <button type="button" className="btn-secondary" onClick={handleClosePaymentModal}>
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
